import { isAgGridColumnVisible, type MobileGroupCardRendererProps, type RenderCellOptions } from '@talos/kyoko';
import type { ReactNode } from 'react';

export const POSITION_EQUIVALENT_COL_ID = 'Equivalent.Amount';
export const POSITION_AVG_COST_COL_ID = 'AvgCost';
export const POSITION_COL_ID = 'Amount';

const REQUIRED_POSITION_METRIC_RENDER_OPTIONS = { overrideColumnVisibility: true } satisfies RenderCellOptions;

export interface PositionMetric {
  colId: string;
  label: string;
  value: ReactNode;
  testId: string;
}

export function getPositionEquivalentLabel(homeCurrency: string): string {
  return `Amount (${homeCurrency})`;
}

export function getRequiredPositionMetrics(
  renderCell: MobileGroupCardRendererProps['renderCell'],
  homeCurrency: string
): PositionMetric[] {
  return [
    {
      colId: POSITION_COL_ID,
      label: 'Position',
      value: renderCell(POSITION_COL_ID, REQUIRED_POSITION_METRIC_RENDER_OPTIONS),
      testId: 'mobile-position-card-amount',
    },
    {
      colId: POSITION_EQUIVALENT_COL_ID,
      label: getPositionEquivalentLabel(homeCurrency),
      value: renderCell(POSITION_EQUIVALENT_COL_ID, REQUIRED_POSITION_METRIC_RENDER_OPTIONS),
      testId: 'mobile-position-card-equivalent-amount',
    },
    {
      colId: POSITION_AVG_COST_COL_ID,
      label: 'Avg Cost',
      value: renderCell(POSITION_AVG_COST_COL_ID, REQUIRED_POSITION_METRIC_RENDER_OPTIONS),
      testId: 'mobile-position-card-avg-cost',
    },
  ];
}

/**
 * Resolves the prominent position metric for mobile position group cards.
 * Prefers home-currency equivalent when that column is visible in the grid;
 * otherwise falls back to the raw Position column.
 */
export function resolveVisiblePositionMetric(
  api: MobileGroupCardRendererProps['api'],
  renderCell: MobileGroupCardRendererProps['renderCell'],
  homeCurrency: string
): PositionMetric | null {
  const equivalentColumn = api.getColumn(POSITION_EQUIVALENT_COL_ID);
  if (equivalentColumn && isAgGridColumnVisible(equivalentColumn)) {
    return {
      colId: POSITION_EQUIVALENT_COL_ID,
      label: getPositionEquivalentLabel(homeCurrency),
      value: renderCell(POSITION_EQUIVALENT_COL_ID),
      testId: 'mobile-position-card-equivalent-amount',
    };
  }

  const amountColumn = api.getColumn(POSITION_COL_ID);
  if (amountColumn && isAgGridColumnVisible(amountColumn)) {
    return {
      colId: POSITION_COL_ID,
      label: 'Position',
      value: renderCell(POSITION_COL_ID),
      testId: 'mobile-position-card-amount',
    };
  }

  return null;
}
